import { mergePlaybookJobs, setPlaybookStreamActive } from './playbook-queue';
import { applyCloneProgress, createCloneProgress, useCloneActivity } from './canvas-clone-progress';
import { useCanvasStore } from './canvas-store';
import type { CanvasImage, AgentTaskGroup, AgentTaskStep } from './canvas-types';

/**
 * Standalone clone runner — extracted from create-variations-panel.tsx so
 * the Ad Library "Clone N Ads" batch action can fire N independent clones in
 * parallel without spawning N Variations panels. The panel still uses its
 * own inline path for the single-image, manual flow.
 *
 * Each call to `runCloneForSource` is a self-contained async task that:
 *   1. Pre-computes 2 placeholders below the source image on the canvas
 *   2. Writes synthetic user+assistant chat messages so the run is visible
 *      in the AskMoji chat sidebar
 *   3. POSTs to /api/canvas/create-variations with `mode: 'clone'`
 *   4. Streams SSE events; on each `variation_complete` adds the new image
 *      to the canvas at its placeholder spot, writes the QA badge, and
 *      appends a thumbnail to the assistant message
 *   5. On `done` marks the assistant message as no longer streaming and
 *      persists the conversation history
 *
 * Multiple calls run completely independently — placeholders, chat messages,
 * and SSE streams all keyed on the source image's id and a per-run id.
 */
export interface RunCloneForSourceArgs {
  source: CanvasImage;
  /** How many clones to generate per source. */
  count: number;
  /** Project id whose canvas the source already lives on. */
  projectId: number;
  /** Brand id used by the server to load product photo + packaging context. */
  brandId: number | null;
  /** v7.53 — which product the clones feature (from the picker's product
   *  step on multi-product brands). Name is for the chat echo only. */
  productId?: number;
  productName?: string;
  /**
   * When true (set by the batch flow), the runner skips its end-of-run
   * fit-to-view nudge — the batch handler does a single batch-wide fit so
   * individual runners don't yank the viewport around as each one finishes.
   */
  suppressAutoFit?: boolean;
  queueOnly?: boolean;
  imageModel?: string;
  imageTier?: string | null;
  imageResolution?: string;
  aspectRatios?: string[];
}

export async function runCloneForSource(args: RunCloneForSourceArgs): Promise<void> {
  const { source, count, projectId, brandId, productId, productName, suppressAutoFit } = args;
  const {
    addImageToCanvas,
    addCanvasPlaceholder,
    resolveCanvasPlaceholder,
    failCanvasPlaceholder,
    _writeMessages,
    _writeQaBadges,
    saveConversationHistory,
    _writeProjectView,
  } = useCanvasStore.getState();

  // --- Placeholder positions ---
  // Lay clones in a horizontal row directly UNDER the source. Each source
  // lives on its own dedicated row in the batch layout (computed by the
  // caller — see canvas-ad-library-view.tsx batch handler), so we don't have
  // to worry about cross-runner x collisions: this runner owns the entire
  // row beneath its source.
  //
  //   [Source]
  //   [Clone 1] [Clone 2] ...
  //
  // Clones inherit the source's display size — same aspect, same scale.
  const GEN_GAP = 24;
  // v6.74 — vertical gap source → clone row. The clone row gets a labeled
  // frame whose top edge sits FRAME_PAD_TOP (52px) ABOVE the clones; at the
  // old 24px the frame + pill drew ON TOP of the source ad (Dan: "it should
  // NOT be overlapping the source ad like that"). 90px leaves the frame
  // fully below the source with clear air.
  const SOURCE_TO_CLONES_GAP = 90;
  const placeholderDisplayW = source.displayWidth;
  const placeholderDisplayH = source.displayHeight;

  const runId = `clone-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
  const placeholderTaskIds: string[] = Array.from({ length: count }, (_, idx) => `${runId}-${idx}`);
  const placeholderPositions: Array<{ x: number; y: number }> = Array.from({ length: count }, (_, idx) => ({
    x: source.x + idx * (placeholderDisplayW + GEN_GAP),
    y: source.y + source.displayHeight + SOURCE_TO_CLONES_GAP,
  }));

  // --- Chat-side mirror ---
  const userMessageId = `msg-user-${runId}`;
  const assistantMessageId = `msg-ai-${runId}`;
  const now = Date.now();
  // v2.37 — give cloning the same agent-style process card as a campaign run
  // (was just a plain "On it — generating…" line). One task group with a
  // "Studying source layout" step + one step per clone, updated live from the
  // step / variation_progress / variation_complete events the server emits.
  const cloneGroupId = `clone-${runId}`;
  const studyStepId = `${cloneGroupId}-study`;
  const cloneStepIds = Array.from({ length: count }, (_, i) => `${cloneGroupId}-gen-${i}`);
  const initialGroup = createCloneProgress(cloneGroupId, count);
  _writeMessages(projectId, (msgs) => [
    ...msgs,
    {
      id: userMessageId,
      role: 'user' as const,
      content: `Clone this ad in ${count} ${args.aspectRatios && args.aspectRatios.length > 1 ? 'formats' : 'variations'}${productName ? ` for ${productName}` : ''}`,
      thumbnails: [source.src],
      timestamp: now,
    },
    {
      id: assistantMessageId,
      role: 'assistant' as const,
      content: '',
      isStreaming: true,
      isAgentMessage: true, // required for the chat to render agentTaskGroups (the process card)
      thumbnails: [],
      timestamp: now + 1,
      agentTaskGroups: [initialGroup],
    },
  ]);

  // Patch a single step in the clone group.
  const setCloneStep = (stepId: string, patch: Partial<AgentTaskStep>) =>
    _writeMessages(projectId, (msgs) => msgs.map((m) =>
      m.id === assistantMessageId
        ? { ...m, agentTaskGroups: (m.agentTaskGroups ?? []).map((g) =>
            g.id === cloneGroupId
              ? { ...g, steps: g.steps.map((s) => (s.id === stepId ? { ...s, ...patch } : s)) }
              : g) }
        : m));
  saveConversationHistory(projectId).catch(() => {});

  let completedCount = 0;
  let qaPassCount = 0;
  // v2.34 — track which variation slots actually produced an image. On 'done'
  // any slot WITHOUT a variation_complete failed server-side (QA exhausted /
  // generation error); without this its placeholder stayed a permanent empty
  // box on the canvas (user: "the first batch never made it in").
  const successIdx = new Set<number>();
  // v6.75 — per-slot failure reasons from the server's variation_progress
  // events, shown on the failed tile + chat step at the done-sweep.
  const failReasons: Record<number, string> = {};
  // v6.76 — the server creates a workflow_runs row for this batch and streams
  // its id; stamping it on the landed tiles gives them a titled "Clone"
  // bounding box (frames key on workflowRunId), persistent across reloads.
  let serverRunId: number | null = null;

  let sawDone = false;
  useCloneActivity.getState().start(projectId, runId);
  try {
    const response = await fetch('/api/canvas/create-variations', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
          assistantMessageId,
          sourceClientId: source.id, queueOnly: args.queueOnly,
          imageModel: args.imageModel, imageTier: args.imageTier, imageResolution: args.imageResolution,
          aspectRatios: args.aspectRatios,
        imageUrl: source.src,
          canvasBounds: { left: Math.min(...placeholderPositions.map(p => p.x)), bottom: Math.max(...placeholderPositions.map(p => p.y + placeholderDisplayH)) },
        count,
        creativity: 50, // ignored in clone mode but server expects a value
        mode: 'clone',
        width: source.originalWidth || 1080,
        height: source.originalHeight || 1080,
        brandId: brandId ?? undefined,
        projectId,
        productId,
      }),
    });

    if (!response.ok || !response.body) {
      const failure = await response.json().catch(() => ({}));
      throw new Error(failure.message || failure.error || 'Clone request failed');
    }

    const reader = response.body.getReader();
      if (projectId) setPlaybookStreamActive(projectId, assistantMessageId, true);
    const decoder = new TextDecoder();
    let sseBuffer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      sseBuffer += decoder.decode(value, { stream: true });
      const blocks = sseBuffer.split('\n\n');
      sseBuffer = blocks.pop() ?? '';

      for (const block of blocks) {
        if (!block.trim()) continue;
        const eventMatch = block.match(/^event: (.+)$/m);
        const dataMatch = block.match(/^data: (.+)$/m);
        if (!eventMatch || !dataMatch) continue;
        const eventType = eventMatch[1].trim();
        let data: Record<string, unknown>;
        try { data = JSON.parse(dataMatch[1]); } catch { continue; }
          if (eventType === 'playbook_queue' && projectId) {
            _writeMessages(projectId, messages => mergePlaybookJobs(messages, [{...data, id: data.jobId ?? data.id, projectId: projectId, assistantMessageId} as any], projectId));
            if(args.queueOnly){
              sawDone=true;
              _writeMessages(projectId,messages=>messages.map(message=>message.id===assistantMessageId?{...message,isStreaming:false}:message));
              await reader.cancel();
              return;
            }
          }
        if (['step', 'variation_progress', 'variation_complete', 'done', 'error'].includes(eventType)) {
          _writeMessages(projectId, msgs => msgs.map(message => message.id === assistantMessageId
            ? {...message, agentTaskGroups: (message.agentTaskGroups ?? []).map(group => group.id === cloneGroupId ? applyCloneProgress(group, eventType, data) : group)} : message));
          saveConversationHistory(projectId).catch(() => {});
        }


        if (eventType === 'canvas_image_pending' && projectId) {
          void useCanvasStore.getState().reconcileImagesFromServer(projectId);
        }
        if (eventType === 'run' && typeof data.runId === 'number') {
          serverRunId = data.runId as number;
          useCanvasStore.getState()._writeCanvasPlaceholders(projectId, placeholders => Object.fromEntries(
            Object.entries(placeholders).map(([id, placeholder]) => [id, placeholderTaskIds.includes(id) ? {...placeholder, workflowRunId: serverRunId} : placeholder]),
          ));
        }
        // v6.99 — the route's catch emits `error` with the real reason; it was
        // never handled, so route-level failures surfaced only as a generic
        // stream death. Throw it so the catch below fails the placeholders
        // with the true message in the console.
        if (eventType === 'error' && typeof data.error === 'string') {
          throw new Error(`Clone server error: ${data.error}`);
        }
        if (eventType === 'step' && data.status === 'completed') {
          setCloneStep(studyStepId, { status: 'success' });
        }
        if (eventType === 'variation_progress' && data.status === 'failed') {
          const vi = data.index as number;
          // v6.75 — keep the server's failure reason so the tile + chat step
          // can say WHY (packaging QA rejection vs timeout), instead of a
          // bare "Clone failed" that contradicts what the user sees on
          // WaveSpeed (the image exists — the gate refused to ship it).
          if (typeof vi === 'number' && typeof data.reason === 'string' && data.reason) {
            failReasons[vi] = (data.reason as string).slice(0, 300);
          }
          if (Number.isInteger(vi) && vi >= 0 && vi < count && !successIdx.has(vi) && placeholderTaskIds[vi]) {
            failCanvasPlaceholder(placeholderTaskIds[vi], failReasons[vi] ?? 'Clone failed', projectId);
          }
          if (typeof vi === 'number' && !successIdx.has(vi) && cloneStepIds[vi]) {
            setCloneStep(cloneStepIds[vi], { status: 'error', error: failReasons[vi] ?? 'Clone failed' });
          }
        }

        if (eventType === 'variation_complete') {
          if (typeof data.index !== 'number' || !Number.isInteger(data.index) || data.index < 0 || data.index >= count || typeof data.imageUrl !== 'string' || !data.imageUrl) continue;
          if (successIdx.has(data.index)) continue;
          const imgUrl = data.imageUrl as string;
          const imgW = (data.width as number) || source.originalWidth || 1080;
          const imgH = (data.height as number) || source.originalHeight || 1080;
          const variationIdx = data.index as number;
          const filename = `clone_${variationIdx}_${Date.now()}.jpg`;
          const adMeta = {
            adStyle: 'Clone',
            conceptName: `Clone ${variationIdx + 1}`,
            wavespeedPrompt: 'Clone of source ad — layout swapped to current brand',
            workflowRunId: serverRunId ?? undefined,
          };
          const taskId = placeholderTaskIds[variationIdx];
          if (taskId && useCanvasStore.getState().projects[projectId ?? -1]?.canvasPlaceholders[taskId]) resolveCanvasPlaceholder(taskId, projectId);
          successIdx.add(variationIdx);
          if (cloneStepIds[variationIdx]) {
            const qp = data.qaPassed;
            setCloneStep(cloneStepIds[variationIdx], { status: 'success', summary: qp === true ? 'QA passed' : qp === false ? 'QA flagged' : undefined });
          }
          const pos = placeholderPositions[variationIdx];
          if (data.serverManaged && projectId) {
            void useCanvasStore.getState().reconcileImagesFromServer(projectId);
          } else addImageToCanvas(
            imgUrl, imgW, imgH, filename,
            imgW, imgH, `Clone ${variationIdx + 1}`,
            pos?.x, pos?.y, adMeta,
            projectId,
          );

          completedCount++;
          const thisQa = data.qaPassed as boolean | null | undefined;
          if (thisQa === true) qaPassCount++;
          const completedNow = completedCount;
          const passedNow = qaPassCount;
          _writeMessages(projectId, (msgs) =>
            msgs.map((m) =>
              m.id === assistantMessageId
                ? {
                    ...m,
                    imageSlots: [...(m.imageSlots ?? []).filter(slot => slot.index !== variationIdx), {index: variationIdx, resolved: imgUrl}].sort((a,b)=>a.index-b.index),
                    thumbnails: [...(m.imageSlots ?? []).filter(slot => slot.index !== variationIdx), {index: variationIdx, resolved: imgUrl}].sort((a,b)=>a.index-b.index).map(slot=>slot.resolved).filter((url):url is string=>!!url),
                    content: completedNow < count
                      ? `${completedNow} of ${count} clones delivered. QA so far: ${passedNow}/${completedNow} passed.`
                      : `Done — ${count} clone${count === 1 ? '' : 's'} generated. QA: ${passedNow}/${count} passed.`,
                  }
                : m,
            ),
          );

          // QA badge persistence — write to in-memory + PATCH the row once
          // its client_id is known.
          const qaPassed = data.qaPassed as boolean | null | undefined;
          const qaReason = data.qaReason as string | null | undefined;
          if (qaPassed !== undefined && qaPassed !== null) {
            const passed = !!qaPassed;
            const reasonStr = qaReason || (passed ? 'QA Passed' : 'QA Failed');
            _writeQaBadges(projectId, (badges) => ({
              ...badges,
              [imgUrl]: { passed, score: 0, badgeType: passed ? 'pass' : 'fail', reason: reasonStr, attempt: 0 },
            }));
            (async () => {
              for (let attempt = 0; attempt < 12; attempt++) {
                await new Promise((r) => setTimeout(r, 250));
                const projState = useCanvasStore.getState().projects[projectId];
                const found = projState?.images?.find((im) => im.src === imgUrl);
                if (found?.id) {
                  try {
                    await fetch(`/api/canvas/projects/${projectId}/images/${found.id}`, {
                      method: 'PATCH',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ qaPassed: passed, qaReason: reasonStr }),
                    });
                  } catch (e) {
                    console.warn('[CloneRunner] qa PATCH failed:', e);
                  }
                  break;
                }
              }
            })();
          }
        }

        if (eventType === 'done') {
          sawDone = true;
          void useCanvasStore.getState().reconcileImagesFromServer(projectId);
          // v2.34 — sweep any slot that never produced an image: fail its
          // placeholder (removes the empty box) and report the partial result
          // so a silently-failed clone batch can't masquerade as complete.
          let failedCount = 0;
          for (let i = 0; i < count; i++) {
            if (!successIdx.has(i)) {
              const why = failReasons[i] ?? 'Clone failed';
              if (placeholderTaskIds[i] && useCanvasStore.getState().projects[projectId ?? -1]?.canvasPlaceholders[placeholderTaskIds[i]]) failCanvasPlaceholder(placeholderTaskIds[i], why, projectId);
              if (cloneStepIds[i]) setCloneStep(cloneStepIds[i], { status: 'error', error: why });
              failedCount++;
            }
          }
          const okCount = count - failedCount;
          // Finalize the process card: study step done, group completed/failed.
          _writeMessages(projectId, (msgs) => msgs.map((m) =>
            m.id === assistantMessageId
              ? { ...m, agentTaskGroups: (m.agentTaskGroups ?? []).map((g) =>
                  g.id === cloneGroupId
                    ? { ...g, status: (failedCount >= count ? 'failed' : 'completed') as AgentTaskGroup['status'],
                        steps: g.steps.map((s) => (s.id === studyStepId && s.status === 'running' ? { ...s, status: 'success' as const } : s)) }
                    : g) }
              : m));
          _writeMessages(projectId, (msgs) =>
            msgs.map((m) => (m.id === assistantMessageId
              ? {
                  ...m,
                  isStreaming: false,
                  ...(failedCount > 0
                    ? { content: `Generated ${okCount} of ${count} clone${count === 1 ? '' : 's'} — ${failedCount} failed.${Object.values(failReasons)[0] ? ` Why: ${Object.values(failReasons)[0]}` : ''} Re-clone to retry the rest.` }
                    : {}),
                }
              : m)),
          );
          saveConversationHistory(projectId).catch(() => {});

          // Auto-fit the canvas to show this runner's column (source + its
          // stacked clones). Skipped when called from the batch flow — the
          // batch handler does a single batch-wide fit so individual runners
          // don't yank the viewport around as each one finishes.
          if (!suppressAutoFit) {
            const lastPos = placeholderPositions[placeholderPositions.length - 1];
            const minX = source.x;
            const maxX = Math.max(source.x + source.displayWidth, ...placeholderPositions.map((p) => p.x + placeholderDisplayW));
            const minY = source.y;
            const maxY = (lastPos?.y ?? source.y + source.displayHeight) + placeholderDisplayH;
            _writeProjectView(projectId, {
              pendingFitToRow: { minX, minY, maxX, maxY },
              isCanvasOpen: true,
            });
          }
          break;
        }
      }
    }
    if (!sawDone) throw new Error('The clone connection ended before the run finished.');
  } catch (err) {
    console.error('[CloneRunner] Error:', err);
    placeholderTaskIds.forEach((tid, index) => { if (!successIdx.has(index) && useCanvasStore.getState().projects[projectId ?? -1]?.canvasPlaceholders[tid]) failCanvasPlaceholder(tid, 'Clone interrupted', projectId); });
    _writeMessages(projectId, (msgs) =>
      msgs.map((m) => (m.id === assistantMessageId
        ? { ...m, isStreaming: false, content: `Cloning stopped: ${err instanceof Error ? err.message : 'The request failed.'}`, agentTaskGroups: (m.agentTaskGroups ?? []).map(group => applyCloneProgress(group, 'error', {})) }
        : m)),
    );
    throw err;
  } finally {
      if (projectId) setPlaybookStreamActive(projectId, assistantMessageId, false);
    useCloneActivity.getState().finish(projectId, runId);
    saveConversationHistory(projectId).catch(() => {});
  }
}
